/**
 * MineraliA favorites (wishlist) stored in localStorage. No account or app required.
 *
 * Elements:
 * - <mineralia-wishlist-button data-product-id data-product-title> with a <button> inside.
 * - <mineralia-wishlist-count> shows the number of saved pieces (hidden when 0).
 *
 * Changes are broadcast with the `mineralia:wishlist-change` event on document
 * (detail: { ids, productId, added }) and synced across tabs through the storage event.
 */

const STORAGE_KEY = 'mineralia-wishlist';
const MAX_ITEMS = 60;
export const WISHLIST_EVENT = 'mineralia:wishlist-change';

export class Wishlist {
  /** @returns {string[]} */
  static getIds() {
    try {
      const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value) ? value.map(String) : [];
    } catch {
      return [];
    }
  }

  /** @param {string} id */
  static has(id) {
    return Wishlist.getIds().includes(String(id));
  }

  /** @param {string[]} ids @param {string} productId @param {boolean} added */
  static #save(ids, productId, added) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids.slice(0, MAX_ITEMS)));
    } catch {
      /* storage unavailable */
    }
    document.dispatchEvent(new CustomEvent(WISHLIST_EVENT, { detail: { ids, productId, added } }));
  }

  /** @param {string} id @returns {boolean} whether the product is now saved */
  static toggle(id) {
    const productId = String(id);
    const ids = Wishlist.getIds();
    if (ids.includes(productId)) {
      Wishlist.#save(
        ids.filter((value) => value !== productId),
        productId,
        false
      );
      return false;
    }
    Wishlist.#save([productId, ...ids], productId, true);
    return true;
  }

  /** @param {string} id */
  static remove(id) {
    const productId = String(id);
    const ids = Wishlist.getIds();
    if (!ids.includes(productId)) return;
    Wishlist.#save(
      ids.filter((value) => value !== productId),
      productId,
      false
    );
  }
}

window.addEventListener('storage', (event) => {
  if (event.key !== STORAGE_KEY) return;
  document.dispatchEvent(new CustomEvent(WISHLIST_EVENT, { detail: { ids: Wishlist.getIds(), productId: null } }));
});

class MineraliaWishlistButton extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #button = null;

  connectedCallback() {
    this.#button = this.querySelector('button');
    if (!this.#button) return;
    this.#button.addEventListener('click', this.#onClick);
    this.addEventListener('animationend', this.#onAnimationEnd);
    document.addEventListener(WISHLIST_EVENT, this.#sync);
    this.#sync();
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#onClick);
    this.removeEventListener('animationend', this.#onAnimationEnd);
    document.removeEventListener(WISHLIST_EVENT, this.#sync);
  }

  /** Clears the one-shot animation state once the heart settles, so it can replay. @param {AnimationEvent} event */
  #onAnimationEnd = (event) => {
    if (!(event.target instanceof Element) || !event.target.classList.contains('mineralia-wishlist-button__icon')) return;
    this.removeAttribute('data-just-added');
    this.removeAttribute('data-just-removed');
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    // Cards are wrapped by links/overlays: keep the click on the button.
    event.preventDefault();
    event.stopPropagation();
    const id = this.dataset.productId;
    if (!id) return;
    const added = Wishlist.toggle(id);
    this.toggleAttribute('data-just-added', added);
    this.toggleAttribute('data-just-removed', !added);
    const status = this.querySelector('[data-wishlist-status]');
    if (status) status.textContent = added ? 'Guardado en favoritos' : 'Quitado de favoritos';
  };

  #sync = () => {
    const pressed = Wishlist.has(this.dataset.productId || '');
    this.#button?.setAttribute('aria-pressed', String(pressed));
    this.toggleAttribute('data-saved', pressed);
    if (!pressed) this.removeAttribute('data-just-added');
    if (pressed) this.removeAttribute('data-just-removed');
  };
}

class MineraliaWishlistCount extends HTMLElement {
  connectedCallback() {
    document.addEventListener(WISHLIST_EVENT, this.#sync);
    this.#sync();
  }

  disconnectedCallback() {
    document.removeEventListener(WISHLIST_EVENT, this.#sync);
  }

  /** @param {CustomEvent} [event] */
  #sync = (event) => {
    const count = Wishlist.getIds().length;
    const label = this.querySelector('[data-wishlist-count]');
    const wasEmpty = this.hasAttribute('data-empty');
    if (label) label.textContent = count > 99 ? '99+' : String(count);
    this.toggleAttribute('data-empty', count === 0);
    if (event?.detail?.added) this.#bump(label, wasEmpty);
    const link = this.closest('a');
    if (link) {
      link.setAttribute('aria-label', count > 0 ? `Favoritos (${count})` : 'Favoritos');
    }
  };

  /**
   * Small bump on the header heart (and the count, unless it is just appearing) when a piece is saved.
   * @param {Element | null} label
   * @param {boolean} wasEmpty
   */
  #bump(label, wasEmpty) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const options = { duration: 420, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' };
    this.querySelector('svg')?.animate(
      [{ transform: 'scale(1)' }, { transform: 'scale(1.18)', offset: 0.4 }, { transform: 'scale(1)' }],
      options
    );
    // Going from empty, the count already scales in with its CSS transition.
    if (!wasEmpty) {
      label?.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.3)', offset: 0.4 }, { transform: 'scale(1)' }], options);
    }
  }
}

if (!customElements.get('mineralia-wishlist-button')) {
  customElements.define('mineralia-wishlist-button', MineraliaWishlistButton);
}
if (!customElements.get('mineralia-wishlist-count')) {
  customElements.define('mineralia-wishlist-count', MineraliaWishlistCount);
}
